import React, { useState, useMemo, useRef } from 'react';
import { FestivalData, Show, Artist } from '../../../../model/FestivalData';
import { FiAlertTriangle, FiClock, FiImage, FiPlus, FiStar, FiUpload } from 'react-icons/fi';
import { generateCanonicalName, generateIdentifier } from '../../../../utils/identifiers';
import {
  formatDay, formatDuration, formatShowTime, formatTime, nightOf, nightStart, toDateInputValue, toTimeInputValue,
  withDate, withTime,
} from '../../../../utils/showTimes';
import { getImageUrl } from '../../../../services/imageService';
import { DataImportService } from '../../../../services/dataImportService';
import DateInput from '../../../../components/DateInput';
import Modal from '../../../../components/Modal';
import { useDialogs } from '../../../../components/Dialogs';
import PageHeader from '../../../../components/PageHeader';
import OverflowMenu from '../../../../components/OverflowMenu';
import SearchField from '../../../../components/SearchField';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import './ShowsEditor.css';

interface ShowsEditorProps {
  shows: FestivalData['shows'];
  artists: FestivalData['artists'];
  pois: FestivalData['pois'];
  onChange: (newShows: FestivalData['shows']) => void;
  getApiImageUrl: (identifier: string) => string;
}

interface ShowValidationErrors {
  dates?: string;
}

/** Height of one hour in the timeline, in pixels */
const HOUR_HEIGHT = 60;
const HOUR = 3600_000;
/** The timeline stops this many hours after the night's noon, however long a show runs */
const LONGEST_NIGHT = 48;
/** How many stage colors ShowsEditor.css defines */
const STAGE_TONES = 6;

const byStartDate = (a: Show, b: Show) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime();

const ShowsEditor: React.FC<ShowsEditorProps> = ({ shows, artists, pois, onChange, getApiImageUrl }) => {
  const t = useMessages('shows');
  const common = useMessages('common');
  const tImport = useMessages('dataImport');
  const { locale } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingShow, setEditingShow] = useState<Show | null>(null);
  const [validationErrors, setValidationErrors] = useState<ShowValidationErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewMode, setViewMode] = useState<'list' | 'timeline'>('list');
  const [selectedNight, setSelectedNight] = useState<string | null>(null);
  const [selectedScene, setSelectedScene] = useState<string>('');
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const { confirm, alert } = useDialogs();

  const artistsById = useMemo(() => new Map(artists.map(artist => [artist.identifier, artist])), [artists]);
  const poisById = useMemo(() => new Map(pois.map(poi => [poi.identifier, poi])), [pois]);

  const filteredShows = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return shows.filter(show => {
      const artist = artistsById.get(show.artist_id);
      const poi = poisById.get(show.scene_id);

      const matchesSearch = !term || (
        (artist?.name.toLowerCase().includes(term) || false) ||
        (poi?.name.toLowerCase().includes(term) || false) ||
        (!artist && t.unknownArtist.toLowerCase().includes(term)) ||
        (!poi && t.unknownStage.toLowerCase().includes(term))
      );

      const matchesScene = !selectedScene || show.scene_id === selectedScene;

      return matchesSearch && matchesScene;
    }).sort(byStartDate);
  }, [shows, artistsById, poisById, searchTerm, selectedScene, t]);

  const scenes = useMemo(() => {
    return pois.filter(poi => poi.category === 'scene');
  }, [pois]);

  /** Every night with a show, in order, with its shows */
  const nights = useMemo(() => {
    const showsByNight = new Map<string, Show[]>();
    [...shows].sort(byStartDate).forEach(show => {
      const night = nightOf(new Date(show.start_date));
      showsByNight.set(night, [...(showsByNight.get(night) ?? []), show]);
    });
    return Array.from(showsByNight, ([night, nightShows]) => ({ night, shows: nightShows }))
      .sort((a, b) => a.night.localeCompare(b.night));
  }, [shows]);

  const currentNight = nights.find(({ night }) => night === selectedNight) ?? nights[0];

  const unsavedShowId = "unsaved_show_id";
  const isNewShow = editingShow?.identifier === unsavedShowId;

  const handleAddShow = () => {
    const newShow: Show = {
      identifier: unsavedShowId,
      scene_id: '',
      artist_id: '',
      start_date: new Date().toISOString(),
      end_date: new Date(new Date().getTime() + 60 * 60 * 1000).toISOString() // Default to 1 hour duration
    };
    setEditingShow(newShow);
  };

  const handleEditShow = (show: Show) => {
    setEditingShow(show);
  };

  const handleToggleFeatured = (show: Show) => {
    onChange(shows.map(item => item.identifier === show.identifier
      ? { ...item, featured: !item.featured }
      : item));
  };

  const closeShow = () => {
    setValidationErrors({});
    setEditingShow(null);
  };

  const handleSaveShow = (updatedShow: Show) => {
    const startDate = new Date(updatedShow.start_date);
    const endDate = new Date(updatedShow.end_date);

    if (startDate >= endDate) {
      setValidationErrors({
        dates: t.startAfterEnd
      });
      return;
    }

    setValidationErrors({});

    const artist = artistsById.get(updatedShow.artist_id);
    const canonicalName = artist
      ? generateCanonicalName(artist.name)
      : 'unknown_artist';

    const newIdentifier = updatedShow.identifier === unsavedShowId
      ? generateIdentifier(canonicalName)
      : updatedShow.identifier;

    const finalShow = {
      ...updatedShow,
      identifier: newIdentifier
    };

    const updatedShows = shows.map(show =>
      show.identifier === updatedShow.identifier ? finalShow : show
    );

    if (!updatedShows.some(show => show.identifier === updatedShow.identifier)) {
      updatedShows.push(finalShow);
    }

    onChange(updatedShows);
    setEditingShow(null);
  };

  const handleRemoveShow = async (identifier: string) => {
    if (await confirm(t.deleteConfirm, { title: t.deleteTitle, confirmLabel: common.delete, danger: true })) {
      const updatedShows = shows.filter(show => show.identifier !== identifier);
      onChange(updatedShows);
      setEditingShow(null);
    }
  };

  const validateShowDates = (show: Show): ShowValidationErrors => {
    const startDate = new Date(show.start_date);
    const endDate = new Date(show.end_date);

    if (startDate >= endDate) {
      return { dates: t.startAfterEnd };
    }
    return {};
  };

  const handleDateTimeChange = (field: 'start_date' | 'end_date', dateOrTime: string, isDate: boolean) => {
    if (editingShow) {
      const currentDate = new Date(editingShow[field]);
      const newDate = isDate ? withDate(currentDate, dateOrTime) : withTime(currentDate, dateOrTime);
      if (!newDate) return; // Invalid date or time, don't update

      const updatedShow = { ...editingShow, [field]: newDate.toISOString() };
      setEditingShow(updatedShow);
      setValidationErrors(validateShowDates(updatedShow));
    }
  };

  const getArtistImageSrc = (artist: Artist): string | undefined => {
    return getImageUrl(artist.main_image, getApiImageUrl);
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = DataImportService.parseJsonFile(content);

        if (!data.shows || !Array.isArray(data.shows)) {
          alert(t.invalidJson, { title: tImport.importFailed });
          return;
        }

        const validShows: Show[] = [];
        const errors: string[] = [];
        let refused = 0;

        data.shows.forEach((show: any) => {
          const result = DataImportService.validateShow(show, artists, pois);
          if (result.isValid) {
            validShows.push(...result.data);
          } else {
            const artist = artists.find(a => a.identifier === show.artist_id);
            const scene = pois.find(p => p.identifier === show.scene_id);
            const identifier = t.importedShow(artist?.name, scene?.name);
            refused += 1;
            errors.push(...result.errors.map(error => `${identifier} ${error}`));
          }
        });

        if (errors.length > 0) {
          console.warn('Validation errors:', errors);
        }

        if (validShows.length === 0) {
          alert(t.noneValid, { title: tImport.importFailed });
          return;
        }

        onChange(validShows);
        alert(t.importReport(validShows.length, refused, errors), { title: tImport.importFinished });
      } catch (error) {
        console.error('Error parsing JSON:', error);
        alert(tImport.parseError, { title: tImport.importFailed });
      }
    };
    reader.readAsText(file);
  };

  const handleClearAllShows = () => {
    onChange([]);
    setShowClearAllModal(false);
  };

  const renderList = () => (
    <div className="data-table">
      <div className="data-table-scroll">
        <div className="data-table-head shows-columns" aria-hidden="true">
          <span>{t.artist}</span><span>{t.stage}</span><span>{t.when}</span>
        </div>
        {filteredShows.map(show => {
          const artist = artistsById.get(show.artist_id);
          const poi = poisById.get(show.scene_id);
          const imageSrc = artist ? getArtistImageSrc(artist) : undefined;

          return (
            <div key={show.identifier} className="show-row-container">
              <button
                type="button"
                className={`data-table-row shows-columns show-row${!artist || !poi ? ' warning' : ''}`}
                onClick={() => handleEditShow(show)}
              >
                <span className="show-artist-cell">
                  {imageSrc
                    ? <img src={imageSrc} alt="" className="show-artist-photo" />
                    : <span className="show-artist-photo missing" aria-hidden="true"><FiImage /></span>}
                  <span className="show-artist">{artist?.name || t.unknownArtist}</span>
                  {(!artist || !poi) && <FiAlertTriangle className="show-warning" role="img" aria-label={t.missingArtistOrStage} />}
                </span>
                <span className="show-stage">{poi?.name || t.unknownStage}</span>
                <span className="show-time">{formatShowTime(new Date(show.start_date), new Date(show.end_date), locale)}</span>
              </button>
              <button
                type="button"
                className="show-featured-toggle"
                aria-label={t.featured}
                aria-pressed={show.featured ?? false}
                title={show.featured ? t.removeFeatured : t.addFeatured}
                onClick={() => handleToggleFeatured(show)}
              >
                <FiStar className="show-featured" aria-hidden="true" />
              </button>
            </div>
          );
        })}
        {filteredShows.length === 0 && (
          <div className="data-table-empty">
            {shows.length === 0 ? t.empty : t.noMatch}
          </div>
        )}
      </div>
    </div>
  );

  const renderTimeline = () => {
    if (!currentNight) {
      return <div className="empty-state">{t.empty}</div>;
    }

    // Hours are counted from the night's noon
    const noon = nightStart(currentNight.night).getTime();
    const hoursFromNoon = (date: string) => (new Date(date).getTime() - noon) / HOUR;
    const firstHour = Math.floor(Math.min(...currentNight.shows.map(show => hoursFromNoon(show.start_date))));
    const lastHour = Math.min(
      LONGEST_NIGHT,
      Math.max(firstHour + 1, Math.ceil(Math.max(...currentNight.shows.map(show => hoursFromNoon(show.end_date))))),
    );
    const hours = Array.from({ length: lastHour - firstHour }, (_, index) => new Date(noon + (firstHour + index) * HOUR));

    // Every stage, then the places of this night's shows that aren't stages (or don't exist any more)
    const columns = scenes.map((scene, index) => ({ id: scene.identifier, name: scene.name, tone: `stage-tone-${index % STAGE_TONES}` }));
    currentNight.shows.forEach(show => {
      if (!columns.some(column => column.id === show.scene_id)) {
        columns.push({ id: show.scene_id, name: poisById.get(show.scene_id)?.name || t.unknownStage, tone: 'stage-tone-unknown' });
      }
    });

    return (
      <div className="timeline">
        <div className="timeline-grid" style={{ gridTemplateColumns: `56px repeat(${columns.length}, minmax(150px, 1fr))` }}>
          <div className="timeline-corner" />
          {columns.map(column => (
            <div key={column.id} className={`timeline-stage ${column.tone}`}>{column.name}</div>
          ))}
          <div className="timeline-hours">
            {hours.map(hour => (
              <div key={hour.getTime()} className={`timeline-hour${hour.getHours() === 0 ? ' midnight' : ''}`}>
                {formatTime(hour)}
              </div>
            ))}
          </div>
          {columns.map(column => (
            <div key={column.id} className={`timeline-column ${column.tone}`} style={{ height: hours.length * HOUR_HEIGHT }}>
              {currentNight.shows.filter(show => show.scene_id === column.id).map(show => {
                const artist = artistsById.get(show.artist_id);
                const top = (hoursFromNoon(show.start_date) - firstHour) * HOUR_HEIGHT;
                const height = (hoursFromNoon(show.end_date) - hoursFromNoon(show.start_date)) * HOUR_HEIGHT;
                return (
                  <button
                    type="button"
                    key={show.identifier}
                    className={`timeline-show${!artist ? ' warning' : ''}`}
                    style={{ top, height }}
                    onClick={() => handleEditShow(show)}
                  >
                    <span className="timeline-show-artist">
                      {!artist && <FiAlertTriangle className="show-warning" role="img" aria-label={t.missingArtistOrStage} />}
                      {artist?.name || t.unknownArtist}
                      {show.featured && <FiStar className="show-featured" role="img" aria-label={t.featured} title={t.featured} />}
                    </span>
                    <span className="timeline-show-time">
                      {formatTime(new Date(show.start_date))} – {formatTime(new Date(show.end_date))}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const editingStart = editingShow ? new Date(editingShow.start_date) : null;
  const editingEnd = editingShow ? new Date(editingShow.end_date) : null;

  return (
    <section className="shows-editor">
      <PageHeader title={t.title} count={shows.length}>
        <label className="btn import-button">
          <input
            type="file"
            accept=".json"
            onChange={handleFileImport}
            ref={fileInputRef}
          />
          <FiUpload aria-hidden="true" />
          {t.importJson}
        </label>
        <button type="button" className="btn btn-primary" onClick={handleAddShow}>
          <FiPlus aria-hidden="true" />
          {t.add}
        </button>
        <OverflowMenu items={[{
          label: t.clearAll,
          danger: true,
          disabled: shows.length === 0,
          onSelect: () => setShowClearAllModal(true),
        }]} />
      </PageHeader>

      <div className="toolbar shows-toolbar">
        {viewMode === 'list' ? (
          <>
            <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={t.search} />
            <select
              className="stage-filter"
              aria-label={t.stage}
              value={selectedScene}
              onChange={(e) => setSelectedScene(e.target.value)}
            >
              <option value="">{t.allStages}</option>
              {scenes.map(scene => (
                <option key={scene.identifier} value={scene.identifier}>
                  {scene.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <div className="night-tabs" role="tablist" aria-label={t.nights}>
            {nights.map(({ night, shows: nightShows }) => (
              <button
                type="button"
                role="tab"
                key={night}
                aria-selected={night === currentNight?.night}
                className="night-tab"
                onClick={() => setSelectedNight(night)}
              >
                <span className="night-tab-day">{formatDay(nightStart(night), locale)}</span>
                <span className="night-tab-count">{t.count(nightShows.length)}</span>
              </button>
            ))}
          </div>
        )}
        <div className="segmented shows-view-toggle">
          <button type="button" aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}>{t.list}</button>
          <button type="button" aria-pressed={viewMode === 'timeline'} onClick={() => setViewMode('timeline')}>{t.timeline}</button>
        </div>
      </div>

      {viewMode === 'list' ? renderList() : renderTimeline()}
      {viewMode === 'timeline' && currentNight && (
        <p className="timeline-note">{t.timelineNote}</p>
      )}

      <Modal isOpen={editingShow !== null} onClose={closeShow} variant="sheet" className="show-sheet">
        {editingShow && editingStart && editingEnd && (
          <>
            <h3>{isNewShow ? t.add : t.edit}</h3>
            <label className="show-field">
              <span>{t.artist}</span>
              <select
                value={editingShow.artist_id}
                onChange={(e) => setEditingShow({ ...editingShow, artist_id: e.target.value })}
              >
                <option value="">{t.selectArtist}</option>
                {artists.map((artist) => (
                  <option key={artist.identifier} value={artist.identifier}>{artist.name}</option>
                ))}
              </select>
            </label>
            <label className="show-field">
              <span>{t.stage}</span>
              <select
                value={editingShow.scene_id}
                onChange={(e) => setEditingShow({ ...editingShow, scene_id: e.target.value })}
              >
                <option value="">{t.selectStage}</option>
                {scenes.map((poi) => (
                  <option key={poi.identifier} value={poi.identifier}>{poi.name}</option>
                ))}
              </select>
            </label>
            <label className="show-featured-field">
              <input
                type="checkbox"
                checked={editingShow.featured ?? false}
                onChange={e => setEditingShow({ ...editingShow, featured: e.target.checked })}
              />
              <span>{t.featured}</span>
            </label>
            <p className="field-description">{t.featuredHelp}</p>
            <div className="show-time-fields">
              <span className="show-field-label">{t.starts}</span>
              <span className="show-field-label">{t.at}</span>
              <DateInput
                type="date"
                aria-label={t.startDate}
                value={toDateInputValue(editingStart)}
                onChange={(value) => handleDateTimeChange('start_date', value, true)}
              />
              <input
                type="time"
                aria-label={t.startTime}
                value={toTimeInputValue(editingStart)}
                onChange={(e) => handleDateTimeChange('start_date', e.target.value, false)}
              />
              <span className="show-field-label">{t.ends}</span>
              <span className="show-field-label">{t.at}</span>
              <DateInput
                type="date"
                aria-label={t.endDate}
                value={toDateInputValue(editingEnd)}
                onChange={(value) => handleDateTimeChange('end_date', value, true)}
              />
              <input
                type="time"
                aria-label={t.endTime}
                value={toTimeInputValue(editingEnd)}
                onChange={(e) => handleDateTimeChange('end_date', e.target.value, false)}
              />
            </div>
            {validationErrors.dates ? (
              <div className="error-message">{validationErrors.dates}</div>
            ) : (
              <div className="show-duration">
                <FiClock aria-hidden="true" />
                {formatDuration(editingStart, editingEnd)}
                {toDateInputValue(editingEnd) !== toDateInputValue(editingStart) && ` · ${t.endsAfterMidnight}`}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => handleSaveShow(editingShow)}
                disabled={!!validationErrors.dates || !editingShow.artist_id || !editingShow.scene_id}
              >
                {common.save}
              </button>
              <button type="button" onClick={closeShow}>{common.cancel}</button>
              {!isNewShow && (
                <button type="button" className="danger" onClick={() => handleRemoveShow(editingShow.identifier)}>
                  {t.remove}
                </button>
              )}
            </div>
          </>
        )}
      </Modal>
      <Modal
        isOpen={showClearAllModal}
        onClose={() => setShowClearAllModal(false)}
      >
        <div className="modal-header">
          <h3>{t.clearAllTitle}</h3>
          <button className="modal-close" onClick={() => setShowClearAllModal(false)} aria-label={common.close}>×</button>
        </div>
        <p>{t.clearAllConfirm(shows.length)}</p>
        <div className="modal-actions">
          <button
            className="modal-delete"
            onClick={handleClearAllShows}
          >
            {t.clearAllButton}
          </button>
          <button
            className="modal-cancel"
            onClick={() => setShowClearAllModal(false)}
          >
            {common.cancel}
          </button>
        </div>
      </Modal>
    </section>
  );
};

export default ShowsEditor;
